import React, { useState, useEffect } from 'react';
import BillingTable from '../Body/BillingTable';
import { Link } from 'react-router-dom';
import BillingTableGeneral from '../Body/BillingTableGeneral';
import { BillableActivitiesService, SalesOrdersService, BillingUtils } from '../Services/billingService';
import DoctorManagementMenu from "../../common/Menu/DoctorManagement";
import TopHeader from "../../common/Header/TopHeader";
import { 
    Button, 
    ButtonGroup, 
    Box, 
    Typography, 
    Paper,
    Chip
} from '@mui/material';
import TableViewIcon from '@mui/icons-material/TableView';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const BillingList = () => {
    // Estado para controlar qué vista mostrar
    const [currentView, setCurrentView] = useState('general'); // 'general' o 'activities'
    
    // Estados principales
    const [billableActivities, setBillableActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Estados para filtros
    const [filters, setFilters] = useState({
        status: [],
        doctor_id: '',
        patient_id: '',
        payer_id: '',
        type: [],
        performed_from: '',
        performed_to: '',
        has_auth: '',
        min_amount: '',
        max_amount: '',
        search: '',
        page: 1,
        page_size: 9999,
        sort: 'performed_at:-1'
    });

    // Estados para paginación
    const [pagination, setPagination] = useState({
        total: 0,
        total_pages: 0,
        current_page: 1,
        page_size: 9999
    });

    // Estados para acciones masivas
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);
    
    // Estados para resumen
    const [summary, setSummary] = useState(null);
    const [showSummary, setShowSummary] = useState(false);

    /**
     * Obtener actividades facturables desde la API (para la vista de actividades específicas)
     */
    const fetchBillableActivities = async (currentFilters = filters) => {
        try {
            console.log('🔄 [BillingList] Iniciando fetchBillableActivities');
            console.log('🔍 [BillingList] Filtros aplicados:', currentFilters);
            
            setIsLoading(true);
            setError(null);
            
            const response = await BillableActivitiesService.getList(currentFilters);
            
            console.log('📡 [BillingList] Respuesta del servicio:', response);
            
            if (response.error) {
                console.error('❌ [BillingList] Error en respuesta:', response.error);
                setError('Error al obtener actividades: ' + response.error.message);
                return;
            }

            // Verificar estructura de respuesta
            if (!response.data) {
                console.error('❌ [BillingList] Estructura de respuesta inválida:', response);
                setError('Respuesta del servidor inválida');
                return;
            }

            console.log('📊 [BillingList] Datos recibidos:', {
                items: response.data.items?.length || 0,
                total: response.data.total,
                page: response.data.page
            });

            // Verificar que items existe y es array
            const items = response.data.items || [];
            if (!Array.isArray(items)) {
                console.error('❌ [BillingList] Items no es array:', items);
                setError('Formato de datos inválido');
                return;
            }

            console.log('🔍 [BillingList] Items a procesar:', items.length);
            if (items.length > 0) {
                console.log('🔍 [BillingList] Primera actividad:', items[0]);
            }

            // Procesar actividades para manejar campos nulos/indefinidos
            const processedActivities = items.map(activity => ({
                ...activity,
                // Verificar que patient_id existe
                patient_id: activity.patient_id || { _id: 'unknown', full_name: 'Paciente no asignado' },
                doctor_id: activity.doctor_id || { _id: 'unknown', full_name: 'Médico no asignado' },
                center_id: activity.center_id || { _id: 'unknown', name: 'Centro no asignado' },
                
                // Verificar estructura amounts
                amounts: {
                    gross: activity.amounts?.gross || 0,
                    tax: activity.amounts?.tax || 0,
                    net: activity.amounts?.net || 0,
                    billed_net: activity.amounts?.billed_net || 0,
                    remaining_net: activity.amounts?.remaining_net || 0
                },
                
                // Verificar arrays
                split: Array.isArray(activity.split) ? activity.split : [],
                so_links: Array.isArray(activity.so_links) ? activity.so_links : []
            }));

            console.log('✅ [BillingList] Actividades procesadas:', processedActivities.length);

            setBillableActivities(processedActivities);
            setPagination({
                total: response.data.total || 0,
                total_pages: response.data.total_pages || 0,
                current_page: response.data.page || 1,
                page_size: response.data.page_size || 9999
            });

            console.log('✅ [BillingList] Estado actualizado correctamente');

        } catch (error) {
            console.error('❌ [BillingList] Error en fetchBillableActivities:', error);
            console.error('❌ [BillingList] Stack:', error.stack);
            setError('Error al cargar las actividades facturables. Por favor, inténtelo de nuevo más tarde.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Obtener resumen de actividades
     */
    const fetchSummary = async () => {
        try {
            const response = await BillableActivitiesService.getSummary(filters);
            if (!response.error) {
                setSummary(response.data);
            }
        } catch (error) {
            console.warn('Error fetching summary:', error);
        }
    };

    /**
     * Manejar cambios en filtros
     */
    const handleFiltersChange = (newFilters) => {
        const updatedFilters = { ...filters, ...newFilters, page: 1 };
        setFilters(updatedFilters);
        
        // Solo hacer fetch si estamos en la vista de actividades específicas
        if (currentView === 'activities') {
            fetchBillableActivities(updatedFilters);
        }
    };

    /**
     * Manejar cambio de página
     */
    const handlePageChange = (newPage) => {
        const updatedFilters = { ...filters, page: newPage };
        setFilters(updatedFilters);
        
        // Solo hacer fetch si estamos en la vista de actividades específicas
        if (currentView === 'activities') {
            fetchBillableActivities(updatedFilters);
        }
    };

    /**
     * Manejar selección de actividades
     */
    const handleActivitySelection = (selectedIds) => {
        setSelectedActivities(selectedIds);
    };

    /**
     * Manejar repricing masivo
     */
    const handleBulkReprice = async (activityIds, force = false) => {
        if (!BillingUtils.canReprice(getCurrentUserRoles())) {
            setError('No tiene permisos para recalcular precios');
            return;
        }

        try {
            setIsProcessingBulkAction(true);
            const response = await BillableActivitiesService.reprice(activityIds, force);
            
            if (response.error) {
                setError('Error en repricing: ' + response.error.message);
                return;
            }

            // Actualizar actividades afectadas solo si estamos en vista de actividades
            if (currentView === 'activities') {
                await fetchBillableActivities();
            }
            setSelectedActivities([]);
            
            // Mostrar resultado
            const results = response.data;
            alert(`Repricing completado:\n- Actualizadas: ${results.updated.length}\n- Omitidas: ${results.skipped.length}\n- Errores: ${results.errors.length}`);

        } catch (error) {
            console.error('Error in bulk reprice:', error);
            setError('Error al recalcular precios');
        } finally {
            setIsProcessingBulkAction(false);
        }
    };

    /**
     * Manejar generación de órdenes de venta
     */
    const handleGenerateSalesOrders = async (generationData) => {
        if (!BillingUtils.canGenerateSalesOrders(getCurrentUserRoles())) {
            setError('No tiene permisos para generar órdenes de venta');
            return;
        }

        try {
            setIsProcessingBulkAction(true);
            
            // Agregar token de idempotencia si no existe
            if (!generationData.idempotency_token) {
                generationData.idempotency_token = BillingUtils.generateIdempotencyToken();
            }

            const response = await SalesOrdersService.generate(generationData);
            
            if (response.error) {
                setError('Error al generar órdenes: ' + response.error.message);
                return;
            }

            // Actualizar actividades afectadas solo si estamos en vista de actividades
            if (currentView === 'activities') {
                await fetchBillableActivities();
            }
            setSelectedActivities([]);

            // Mostrar resultado exitoso
            const results = response.data;
            alert(`Órdenes generadas exitosamente:\n- ${results.summary.orders_created} órdenes creadas\n- ${results.summary.lines_created} líneas generadas\n- ${results.summary.activities_processed} actividades procesadas`);

        } catch (error) {
            console.error('Error generating sales orders:', error);
            setError('Error al generar las órdenes de venta');
        } finally {
            setIsProcessingBulkAction(false);
        }
    };

    /**
     * Manejar marcar como no facturable
     */
    const handleMarkNonBillable = async (activityId, reasonCode, comment = null) => {
        if (!BillingUtils.canMarkNonBillable(getCurrentUserRoles())) {
            setError('No tiene permisos para marcar como no facturable');
            return;
        }

        try {
            const response = await BillableActivitiesService.markNonBillable(activityId, reasonCode, comment);
            
            if (response.error) {
                setError('Error al marcar como no facturable: ' + response.error.message);
                return;
            }

            // Actualizar la lista solo si estamos en vista de actividades
            if (currentView === 'activities') {
                await fetchBillableActivities();
            }
            alert('Actividad marcada como no facturable exitosamente');

        } catch (error) {
            console.error('Error marking non-billable:', error);
            setError('Error al marcar como no facturable');
        }
    };

    /**
     * Manejar actualización de actividad individual
     */
    const handleUpdateActivity = async (activityId, activityData) => {
        try {
            const response = await BillableActivitiesService.update(activityId, activityData);
            
            if (response.error) {
                setError('Error al actualizar actividad: ' + response.error.message);
                return;
            }

            // Actualizar la actividad en el estado local solo si estamos en vista de actividades
            if (currentView === 'activities') {
                setBillableActivities(prev => 
                    prev.map(activity => 
                        activity._id === activityId ? { ...activity, ...response.data } : activity
                    )
                );
            }

        } catch (error) {
            console.error('Error updating activity:', error);
            setError('Error al actualizar la actividad');
        }
    };

/**
 * Manejar creación de actividades facturables desde actividades originales
 */
const handleCreateBillableActivities = async (selectedActivities) => {
    if (!BillingUtils.canCreateBillableActivities || !BillingUtils.canCreateBillableActivities(getCurrentUserRoles())) {
        setError('No tiene permisos para crear actividades facturables');
        return;
    }

    try {
        setIsProcessingBulkAction(true);
        
        console.log('🔄 [handleCreateBillableActivities] Creando actividades facturables para:', selectedActivities.length, 'actividades');
        
        const activitiesToCreate = selectedActivities.map(activity => ({
            element_id: activity.id,
            module_id: activity.source_type || activity.activity_type,
            description: activity.description,
            performed_at: activity.performed_at_raw || activity.performed_at,
            patient_id: activity.patient_id,
            doctor_id: activity.doctor_id,
            center_id: activity.center_id,
        }));

        const response = await BillableActivitiesService.createFromOriginal(activitiesToCreate);
        
        if (response.error) {
            setError('Error al crear actividades facturables: ' + response.error.message);
            return;
        }

        const results = response.data;
        alert(`Actividades facturables creadas exitosamente:\n- Creadas: ${results.created || activitiesToCreate.length}\n- Omitidas: ${results.skipped || 0}\n- Errores: ${results.errors?.length || 0}`);
        
        // 🆕 NUEVA LÍNEA: Refrescar todas las vistas
        await handleRefreshAll();
        
    } catch (error) {
        console.error('❌ [handleCreateBillableActivities] Error:', error);
        setError('Error al crear las actividades facturables: ' + error.message);
    } finally {
        setIsProcessingBulkAction(false);
    }
};


    /**
     * Manejar creación de factura (para la vista general)
     */
    const handleCreateInvoice = async (invoiceData) => {
        try {
            // Aquí implementarías la lógica para crear facturas
            // usando el servicio correspondiente
            console.log('Creando factura con datos:', invoiceData);
            
            // Ejemplo de implementación:
            // const response = await InvoiceService.create(invoiceData);
            // if (response.error) {
            //     throw new Error(response.error.message);
            // }
            
            alert('Factura creada exitosamente');
            
        } catch (error) {
            console.error('Error creating invoice:', error);
            throw error;
        }
    };

    /**
     * Obtener roles del usuario actual (simulado - implementar según tu sistema de auth)
     */
    const getCurrentUserRoles = () => {
        // TODO: Implementar obtención real de roles desde tu sistema de autenticación
        const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
        return userData.roles || [];
    };

    /**
     * Limpiar errores
     */
    const clearError = () => {
        setError(null);
    };

    /**
 * Refrescar datos de ambas vistas
 */
const handleRefreshAll = async () => {
    console.log('🔄 [BillingList] Refrescando todas las vistas');
    
    // Refrescar vista de actividades procesadas si estamos en esa vista o tiene datos
    if (currentView === 'activities' || billableActivities.length > 0) {
        await fetchBillableActivities();
    }
    
    // La vista general se refresca automáticamente a través de su prop refreshData
    // pero podemos forzar una señal de recarga
    console.log('✅ [BillingList] Recarga completada');
};

    /**
 * Manejar cambio de vista
 */
const handleViewChange = async (newView) => {
    console.log(`🔄 [BillingList] Cambiando vista a: ${newView}`);
    setCurrentView(newView);
    setSelectedActivities([]); // Limpiar selecciones al cambiar vista
    setError(null); // Limpiar errores
    
    // 🆕 SIEMPRE RECARGAR DATOS AL CAMBIAR A LA VISTA DE ACTIVIDADES PROCESADAS
    if (newView === 'activities') {
        console.log('🔄 [BillingList] Recargando datos de actividades procesadas...');
        setIsLoading(true); // Mostrar loading mientras carga
        await fetchBillableActivities();
        console.log('✅ [BillingList] Datos de actividades procesadas recargados');
    }
};

    // Efectos
    useEffect(() => {
        // Solo cargar datos iniciales si estamos en la vista de actividades específicas
        if (currentView === 'activities') {
            fetchBillableActivities();
        }
    }, []); // Solo al montar el componente

    useEffect(() => {
        if (showSummary && currentView === 'activities') {
            fetchSummary();
        }
    }, [filters, showSummary, currentView]);

    // 🆕 NUEVO useEffect - Recargar datos al cambiar de vista
useEffect(() => {
    console.log(`🔄 [BillingList] Vista cambió a: ${currentView}`);
    
    if (currentView === 'activities') {
        console.log('🔄 [BillingList] Recargando actividades procesadas...');
        fetchBillableActivities();
    }
    // No necesitamos hacer nada para 'general' porque esa tabla maneja su propia carga
}, [currentView]); // Se ejecuta cada vez que cambia currentView

    return (
        <main id="app">
            <TopHeader />
            <div className="content_wrapper">
            <div class="doctor-management-wrapper bg-white pb-5">
                 <DoctorManagementMenu activeMenu='/billable-activities' />
                
                
                    
                    <div className="container-fluid px-lg-5">
    {/* Breadcrumb y Selector de Vista en la misma línea */}
    <Box sx={{ 
        
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
    }}>
        {/* Breadcrumb */}
        <nav className="breadcrumb-container" aria-label="breadcrumb">
            <ol className="breadcrumb fs-sm mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">
                    {currentView === 'general' 
                        ? 'All Patients Activities' 
                        : 'Billable Activities'
                    }
                </li>
            </ol>
        </nav>
        
        {/* Selector de vista */}
        <Paper elevation={1} sx={{ p: 0.5 }}>
            <ButtonGroup variant="contained" color="primary" size="small">
                <Button
                    variant={currentView === 'general' ? 'contained' : 'outlined'}
                    startIcon={<TableViewIcon />}
                    onClick={() => handleViewChange('general')}
                    sx={{ minWidth: '120px' }}
                >
                    Todas
                </Button>
                <Button
                    variant={currentView === 'activities' ? 'contained' : 'outlined'}
                    startIcon={<ReceiptLongIcon />}
                    onClick={() => handleViewChange('activities')}
                    sx={{ minWidth: '120px' }}
                >
                    Procesadas
                </Button>
            </ButtonGroup>
        </Paper>
    </Box>

    

                    

                    {/* Tabla principal - Renderizado condicional */}
                    <section>
                        {currentView === 'general' ? (
    <BillingTableGeneral
        currentUserRoles={getCurrentUserRoles()}
        onCreateBillableActivities={handleCreateBillableActivities}
        onActivityUpdate={handleUpdateActivity}
        refreshData={handleRefreshAll}  // 🆕 NUEVA PROP
    />
) : (
    <BillingTable
    activities={billableActivities}
    isLoading={isLoading}
    pagination={pagination}
    filters={filters}
    selectedActivities={selectedActivities}
    isProcessingBulkAction={isProcessingBulkAction}
    onFiltersChange={handleFiltersChange}
    onPageChange={handlePageChange}
    onActivitySelection={handleActivitySelection}
    onBulkReprice={handleBulkReprice}
    onGenerateSalesOrders={handleGenerateSalesOrders}
    onMarkNonBillable={handleMarkNonBillable}
    onUpdateActivity={handleUpdateActivity}
    onReloadData={handleRefreshAll}  // ✅ AGREGAR ESTA LÍNEA
    currentUserRoles={getCurrentUserRoles()}
    currentCenterId={null}  // ✅ AGREGAR ESTA LÍNEA (o pasarle el centro real si lo tienes)
/>
)}
                    </section>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default BillingList;